
import {groupBy, sortBy, values, flatten} from 'lowscore'
import {get} from 'lodash-es'
import sum from 'root-core/utils/array/sum.js'
import expandPassengers from '../expandPassengers.js'
import * as roomsKey from './roomsKey.js'
import {isSharedRoom, getBaseRoomType} from './sharedRoomsUtils.js'

const singleAdultPreferredRoomOrder = [
  'multiShare',
  'twinShare',
  'quadShare',
  'tripleShare',
  'single',
]

class RoomPermutations {
  constructor (accommodation, priceEngine, config, passengers = []) {
    this.accommodation = accommodation
    this.priceEngine = priceEngine
    this.config = config
    this.passengers = passengers

    this.roomsByAllocation = RoomPermutations.buildRoomsByAllocation(
      this.accommodation,
      get(this, 'config.features.childrenAllocationPolicy.teenagers.rooms')
    )
  }

  bestRoomAllocations ({adults, children, teenagers = 0}, rooms) {
    const roomAllocationsWithoutSharesOfSamePrice = this.allRoomAllocationsWithoutSharesOfSamePrice(
      adults,
      children,
      teenagers,
      rooms
    )

    const roomAllocationsWithGroupedRooms = RoomPermutations.groupRoomsInRoomAllocations(
      roomAllocationsWithoutSharesOfSamePrice
    )

    const sorted = adults + children + teenagers == 1
      ? RoomPermutations.roomAllocationsOrderedForSinglePassenger(roomAllocationsWithGroupedRooms)
      : RoomPermutations.roomAllocationsOrderedForMultiplePassengers(roomAllocationsWithGroupedRooms)

    if (this.accommodation.recommendedRoomAllocation) {
      const selectedRoomAllocation = this.findRoomAllocationOrReturnDefault(
        sorted,
        this.accommodation.recommendedRoomAllocation
      )
      const index = sorted.indexOf(selectedRoomAllocation)

      if (index >= 0) {
        sorted.splice(index, 1)
        sorted.splice(0, 0, selectedRoomAllocation)
      }
    }

    return sorted
  }

  allRoomAllocationsWithoutSharesOfSamePrice (adults, children, teenagers, rooms) {
    return RoomPermutations.removeRoomSharesWhichHaveNoAdvantageOverAnother(
      this.allRoomAllocationsWithPrice(adults, children, teenagers, rooms)
    )
  }

  areRoomsAvailableForParty ({adults, children, teenagers}) {
    return RoomPermutations.enumerateRoomAllocations(adults, children, teenagers, this.roomsByAllocation).length > 0
  }

  allRoomAllocationsWithPrice (adults, children, teenagers, requestedRooms) {
    const sortedRequestedRoomTypes = requestedRooms && requestedRooms.map(r => r.roomType).sort()
    const roomAllocationsWithPrice = RoomPermutations.enumerateRoomAllocations(
      adults,
      children,
      teenagers,
      this.roomsByAllocation
    ).filter(rooms =>
      !requestedRooms ||
      rooms.map(r => r.roomType).sort().toString() == sortedRequestedRoomTypes.toString()
    ).map(rooms => {
      const normalizedRooms = rooms.map(room => ({
        ...room,
        children: room.children + room.teens,
        teens: undefined,
      }))
      const price = this.priceEngine.price(
        {
          party: {
            adults,
            children: children + teenagers,
          },
          rooms: normalizedRooms,
          sellingCompany: this.config,
        },
        {
          features: this.config && this.config.features,
          passengers: this.passengers,
        },
        new Date()
      )
      rooms.forEach(room => {
        const pricedRoom = price.rooms.find(r => r.roomType == room.roomType)
        const adultPassenger = pricedRoom && pricedRoom.passengers.find(p => p.type == 'adult')
        const childPassenger = pricedRoom && pricedRoom.passengers.find(p => p.type == 'child')
        room.adultPrice = adultPassenger?.total
        room.childPrice = childPassenger?.total
      })

      const totalWithoutOptionalDiscounts = values(price.discounts)
        .reduce((res, d) => res + d.amount, price.total)

      const {roomsOccupancy} = this.priceEngine.departure

      return {
        price: totalWithoutOptionalDiscounts,
        rooms: normalizedRooms,
        status: ['closed', 'onRequest'].find(status => rooms.some(r => roomsOccupancy[getBaseRoomType(r)] === status)) || 'available'
      }
    })
    return roomAllocationsWithPrice
  }

  findRoomAllocationOrReturnDefault (roomAllocations, selectedRooms) {
    const defaultRoomAllocation = roomAllocations[0]
    const found = this.findRoomAllocation(roomAllocations, selectedRooms)
    return found || defaultRoomAllocation
  }

  findRoomAllocation (roomAllocations, roomAllocation) {
    if (roomAllocations && roomAllocation) {
      const key = roomsKey.stringify(roomAllocation, true)
      return roomAllocations.find(ra => {
        return roomsKey.stringify(ra.rooms, true) == key
      })
    }
  }

  static enumerateAllocations (adults, children, teens = 0) {
    function enumerateUniqueAllocations (adults, maxAdults, children, maxChildren, teens, maxTeens, callback) {
      if (adults === 0 || teens < 0 || children < 0) {
        return
      }

      for (let adultEnumerator = 1; adultEnumerator <= adults; adultEnumerator++) {
        for (let teenEnumerator = 0; teenEnumerator <= teens; teenEnumerator++) {
          for (let childEnumerator = 0; childEnumerator <= children; childEnumerator++) {
            if (
              adultEnumerator > maxAdults ||
              (adultEnumerator === maxAdults && teenEnumerator > maxTeens) ||
              (adultEnumerator === maxAdults && teenEnumerator === maxTeens && childEnumerator > maxChildren)
            ) {
              return
            }

            if (adultEnumerator === adults && teenEnumerator === teens && childEnumerator === children) {
              return callback([{adults: adultEnumerator, teens: teenEnumerator, children: childEnumerator}])
            }
            enumerateUniqueAllocations(
              adults - adultEnumerator,
              adultEnumerator,
              children - childEnumerator,
              childEnumerator,
              teens - teenEnumerator,
              teenEnumerator,
              allocation => {
                callback([{adults: adultEnumerator, teens: teenEnumerator, children: childEnumerator}].concat(allocation))
              }
            )
          }
        }
      }
    }

    const allocations = []

    const callback = (allocation) => {
      allocations.push(allocation)
    }
    enumerateUniqueAllocations(adults, adults, children, children, teens, teens, callback)

    return allocations
  }

  static uniqueCartesianProduct (products, id) {
    const items = []
    const alreadyDone = {}

    function isAlreadyDone (results) {
      const ids = id ? results.map(id) : results

      const key = ids.sort().join(':')
      const done = alreadyDone[key]
      alreadyDone[key] = true
      return done
    }

    function enumerate (index, results) {
      if (index < products.length) {
        const product = products[index]
        const limit = product.length

        for (let i = 0; i < limit; i++) {
          const orderProperty = product[i]
          enumerate(index + 1, results.concat([orderProperty]))
        }
      } else if (!isAlreadyDone(results)) {
        items.push(results)
      }
    }

    enumerate(0, [])

    return items
  }

  static buildRoomsByAllocation ({occupancyRules = {}}, teenRooms = []) {
    const rooms = flatten(Object.keys(occupancyRules).map(roomType => {
      let allocations = []
      let room = occupancyRules[roomType]
      for (let adults = room.minAdults; adults <= room.maxAdults; adults++) {
        const isTeenRestrictedRoom = teenRooms.includes(roomType)
        for (let teens = 0; adults + teens <= (isTeenRestrictedRoom ? room.maxAdults : room.maxPassengers); teens++) {
          const minChildren = Math.max(room.minPassengers - adults - teens, 0)
          for (let children = minChildren; adults + teens + children <= room.maxPassengers; children++) {
            const allocation = {
              roomType: roomType,
              adults,
              teens,
              children,
              passengers: expandPassengers(adults, children + teens),
              maxPassengers: room.maxPassengers,
            }
            if (isSharedRoom(roomType)) {
              allocation.share = true
            }
            allocations.push(allocation)
          }
        }
      }
      return allocations
    }))
    return groupBy(rooms, room => `${room.adults}:${room.children}:${room.teens}`)
  }

  static enumerateRoomAllocations (adults, children, teenagers = 0, rooms) {
    const possibleAllocations = []
    const allocations = RoomPermutations.enumerateAllocations(adults, children, teenagers)
    allocations.forEach(groupAllocation => {
      const possibleRoomAllocations = groupAllocation
        .map(allocation => rooms[`${allocation.adults}:${allocation.children}:${allocation.teens}`])
        .filter(Boolean)

      if (possibleRoomAllocations.length == groupAllocation.length) {
        const cartesianProduct = RoomPermutations.uniqueCartesianProduct(
          possibleRoomAllocations,
          room => room.roomType
        )
        possibleAllocations.push(...cartesianProduct)
      }
    })
    return possibleAllocations
  }

  static removeRoomSharesWhichHaveNoAdvantageOverAnother (roomAllocations) {
    const withoutShares = RoomPermutations.removeRoomSharesWhichAreAlwaysInferiorToAnother(roomAllocations)
    return RoomPermutations.preferCheapestRoomAllocationsWithSameRooms(withoutShares)
  }

  static removeRoomSharesWhichAreAlwaysInferiorToAnother (roomAllocations) {
    const result = []
    roomAllocations.forEach(function (roomAllocation) {
      if (
        !roomAllocations.filter(compareTo => {
          return RoomPermutations.roomAllocationHasNoAdvantageOverAnother(
            roomAllocation,
            compareTo
          )
        })[0]
      ) {
        result.push(roomAllocation)
      }
    })

    return result
  }

  static roomAllocationHasNoAdvantageOverAnother (roomAllocation, compareTo) {
    const containsTheSameCompleteRooms = RoomPermutations.getCompleteRoomsAsKey(compareTo).indexOf(RoomPermutations.getCompleteRoomsAsKey(roomAllocation)) != -1
    const hasLessRoomShares = RoomPermutations.getShares(compareTo).length < RoomPermutations.getShares(roomAllocation).length
    const isCheaperOrEqualPrice = compareTo.price <= roomAllocation.price

    return containsTheSameCompleteRooms && isCheaperOrEqualPrice && hasLessRoomShares
  }

  static roomsAsKey (rooms) {
    return rooms.map(r => r.roomType).join(',')
  }

  static getShares (roomAllocation) {
    return roomAllocation.rooms.filter(r => r.share)
  }

  static getCompleteRooms (roomAllocation) {
    return roomAllocation.rooms.filter(r => !r.share)
  }

  static getCompleteRoomsAsKey (roomAllocation) {
    return RoomPermutations.roomsAsKey(RoomPermutations.getCompleteRooms(roomAllocation))
  }

  static preferCheapestRoomAllocationsWithSameRooms (roomAllocations) {
    const uniqueRoomAllocations = {}
    roomAllocations.forEach(roomAllocation => {
      const key = sortBy(roomAllocation.rooms, 'roomType')
        .map(room => room.roomType)
        .join(',')
      const existing = uniqueRoomAllocations[key]
      if (!existing || roomAllocation.price < existing.price) {
        uniqueRoomAllocations[key] = roomAllocation
      }
    })

    return Object.keys(uniqueRoomAllocations).map(k => uniqueRoomAllocations[k])
  }

  static groupRoomsInRoomAllocations (roomAllocations) {
    return roomAllocations.map((roomAllocation) => {
      return {
        price: roomAllocation.price,
        rooms: RoomPermutations.groupRooms(roomAllocation.rooms),
        status: roomAllocation.status,
      }
    })
  }

  static groupRooms (rooms) {
    let grouped = groupBy(rooms, (room) => {
      return room.roomType + ':' + room.adults + ':' + room.children
    })

    let groupedRooms = Object.keys(grouped).map((key) => {
      let rooms = grouped[key]
      let room = rooms[0]

      return {
        roomType: room.roomType,
        adults: room.adults,
        children: room.children + (room.teens || 0),
        share: room.share,
        count: rooms.length,
        maxPassengers: room.maxPassengers,
      }
    })

    groupedRooms.sort(function (left, right) {
      let leftPeople = left.adults + left.children
      let rightPeople = right.adults + right.children

      if (leftPeople > rightPeople || !left.share && right.share) {
        return -1
      } else if (leftPeople < rightPeople) {
        return 1
      } else {
        return 0
      }
    })

    return groupedRooms
  }

  static roomAllocationsOrderedForSinglePassenger (roomAllocationsWithGroupedRooms) {
    let sorted = []
    singleAdultPreferredRoomOrder.forEach(pr => {
      const orderedRoom = roomAllocationsWithGroupedRooms.find(r => r.rooms[0].roomType == pr)
      if (orderedRoom) {
        sorted.push(orderedRoom)
      }
    })
    if(sorted.length > 1) {
      sorted.sort((a, b) => {
        return RoomPermutations.closedRoomWeighting(a, b)
      })
    }
    return sorted
  }

  static roomAllocationsOrderedForMultiplePassengers (roomAllocationsWithGroupedRooms) {
    return roomAllocationsWithGroupedRooms.sort((a, b) => {
      let soloOccupantWeightingA = RoomPermutations.soloOccupantWeighting(a)
      let soloOccupantWeightingB = RoomPermutations.soloOccupantWeighting(b)
      if (soloOccupantWeightingA < soloOccupantWeightingB) {
        return -1
      }
      if (soloOccupantWeightingA > soloOccupantWeightingB) {
        return 1
      }
      return a.price - b.price
    })
      .sort(RoomPermutations.closedRoomWeighting)
  }

  static soloOccupantWeighting (roomAllocation) {
    let shares = sum(
      roomAllocation.rooms.filter(ra => ra.share).map(ra => ra.count)
    )
    let singles = sum(
      roomAllocation.rooms
        .filter(ra => ra.roomType === 'single')
        .map(ra => ra.count)
    )
    return shares + singles
  }

  static closedRoomWeighting (a, b) {
    if (a.status !== 'closed' && b.status === 'closed') {
      return -1
    }
    if (a.status === 'closed' && b.status !== 'closed') {
      return 1
    }
  }
}

export default RoomPermutations